"use client"

import {
  OrderWidgetLayout,
  OrderWidgetSkeleton,
} from "@/app/item/view/item-order-widget"
import { PriceInCrypto, PriceInUsd, PriceWrapper } from "@/app/price"
import { SDKNFTScope } from "@/app/sdk/anaconda-sdk-scope"
import { formatDate } from "@/app/utils/format-date"
import type { NftOrder } from "@anaconda/core/nft/types"
import { getListingEndTime } from "@anaconda/core/order/listing-liveness"
import { Trans } from "@lingui/react/macro"
import { reatomComponent } from "@reatom/react"
import { ScopeProvider } from "bunshi/react"
import Image from "next/image"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useNftReatom } from "../../store/hooks.reatom"
import { PrimaryAction, SecondaryAction } from "./actions/actions"
import { PriceStatistic } from "./price-statistic"

function isRaribleOrder(order?: NftOrder): boolean {
  if (!order?.data) return false
  const orderType = order.data["@type"]
  return orderType === "ETH_RARIBLE_V2_2" || orderType === "ETH_RARIBLE_V2_3"
}

function getRaribleVersion(order?: NftOrder): string | null {
  if (!order?.data) return null
  const orderType = order.data["@type"]
  if (orderType === "ETH_RARIBLE_V2_2") return "v2"
  if (orderType === "ETH_RARIBLE_V2_3") return "v3"
  return null
}

export function NftOrderWidgetReatom() {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<OrderWidgetSkeleton />}>
        <NftOrderWidgetContentReatom />
      </Suspense>
    </ErrorBoundary>
  )
}

const NftOrderWidgetContentReatom = reatomComponent(() => {
  const nft = useNftReatom()
  const raribleVersion = getRaribleVersion(nft.bestSellOrder)
  // Undefined for a listing without an end, which Union reads as 1970.
  const listingEnd = nft.bestSellOrder
    ? getListingEndTime(nft.bestSellOrder)
    : undefined

  return (
    <ScopeProvider scope={SDKNFTScope} value={nft}>
      <OrderWidgetLayout
        header={
          <div className="flex flex-wrap items-center justify-between gap-1 text-foreground/60">
            <Trans>Listed for</Trans>
            <div className="flex items-baseline gap-2">
              {listingEnd !== undefined && (
                <span className="text-xs">
                  <Trans>Expires</Trans> {formatDate(new Date(listingEnd))}
                </span>
              )}
              {raribleVersion && (
                <div className="flex items-center gap-1">
                  <Image
                    src="/images/orders/rarible-order.svg"
                    alt="Rarible"
                    width={16}
                    height={16}
                    className="inline-block"
                  />
                </div>
              )}
            </div>
          </div>
        }
        price={
          nft.price ? (
            <div className="flex items-baseline gap-3">
              <PriceInUsd className=" font-headline text-2xl" {...nft.price} />
              <PriceInCrypto className="[&&]:text-base" {...nft.price} />
            </div>
          ) : (
            <h3 className="text-2xl">
              <Trans>Not Listed</Trans>
            </h3>
          )
        }
        priceInfo={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-nowrap text-foreground/60">
            {nft.lastSellPrice && (
              <span>
                <Trans>Last:</Trans>{" "}
                <PriceWrapper {...nft.lastSellPrice} className="text-base" />
              </span>
            )}
            <PriceStatistic nft={nft} />
          </div>
        }
        primaryAction={<PrimaryAction nft={nft} />}
        secondaryAction={<SecondaryAction nft={nft} />}
      />
    </ScopeProvider>
  )
})
